import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Loader2, Send } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { currentSubscription, disablePush, enablePush, pushSupported } from "@/lib/push";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";

export const PushToggle = () => {
  const { t } = useI18n();
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const supported = pushSupported();
  useEffect(() => {
    if (supported) currentSubscription().then((s) => setOn(Boolean(s))).catch(() => {});
  }, [supported]);
  const toggle = async (next) => {
    setBusy(true);
    try {
      if (next) await enablePush();
      else await disablePush();
      setOn(next);
      toast.success(t(next ? "cap.pushOn" : "cap.pushOff"));
    } catch (err) {
      toast.error(err?.message === "denied" ? t("cap.pushDenied") : translateError(err, t));
    } finally {
      setBusy(false);
    }
  };
  const test = async (level) => {
    try {
      await api.post("/admin/capacity/push/test", { level });
      toast.success(t("cap.testSent"));
    } catch (err) {
      toast.error(translateError(err, t));
    }
  };
  return (
    <section className="card-carbon rounded-2xl p-5" data-testid="capacity-push-card">
      <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-white"><BellRing className="h-4 w-4 text-[var(--gold)]" /> {t("cap.pushTitle")}</h2>
      {!supported ? (
        <p className="mt-3 text-sm text-neutral-400" data-testid="capacity-push-unsupported">{t("cap.pushUnsupported")}</p>
      ) : (
        <>
          <label className="mt-3 flex items-center justify-between gap-3 text-sm text-neutral-200">
            <span>{t("cap.pushToggle")}</span>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Switch checked={on} onCheckedChange={toggle} data-testid="capacity-push-toggle" />}
          </label>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" className="btn-gold btn-sm" onClick={() => test("orange")} disabled={!on} data-testid="capacity-push-test">
              <Send className="h-4 w-4 rtl:-scale-x-100" /> {t("cap.pushTest")}
            </button>
            <button type="button" className="btn-red btn-sm" onClick={() => test("red")} disabled={!on} data-testid="capacity-push-test-red">
              <Send className="h-4 w-4 rtl:-scale-x-100" /> {t("cap.pushTestRed")}
            </button>
          </div>
          <p className="mt-2 text-xs text-neutral-500">{t("cap.pushHint")}</p>
        </>
      )}
    </section>
  );
};
